import { motion } from "motion/react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";

const COLORS = ["#9B87F5", "#7E69AB", "#6E59A5", "#D6BCFA", "#8B5CF6"];

interface ClicksByDateChartProps {
  data: Record<string, number>;
}

export function ClicksByDateChart({ data }: ClicksByDateChartProps) {
  const chartData = Object.entries(data)
    .map(([date, clicks]) => ({
      date: new Date(date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
      clicks,
    }))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  if (chartData.length === 0) {
    return (
      <div className="h-[200px] flex items-center justify-center text-[#8E9196] font-mono text-sm">
        NO_DATA_AVAILABLE
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={200}>
      <AreaChart data={chartData}>
        <defs>
          <linearGradient id="colorClicks" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#9B87F5" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#9B87F5" stopOpacity={0} />
          </linearGradient>
        </defs>
        <XAxis
          dataKey="date"
          stroke="#8E9196"
          fontSize={10}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          stroke="#8E9196"
          fontSize={10}
          tickLine={false}
          axisLine={false}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: "#1A1F2C",
            border: "1px solid #9B87F5",
            borderRadius: "8px",
            fontFamily: "monospace",
          }}
          labelStyle={{ color: "#9B87F5" }}
          itemStyle={{ color: "#fff" }}
        />
        <Area
          type="monotone"
          dataKey="clicks"
          stroke="#9B87F5"
          strokeWidth={2}
          fillOpacity={1}
          fill="url(#colorClicks)"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

interface DeviceChartProps {
  data: Record<string, number>;
}

export function DeviceChart({ data }: DeviceChartProps) {
  const chartData = Object.entries(data).map(([name, value]) => ({
    name,
    value,
  }));

  if (chartData.length === 0) {
    return (
      <div className="h-[200px] flex items-center justify-center text-[#8E9196] font-mono text-sm">
        NO_DATA_AVAILABLE
      </div>
    );
  }

  return (
    <div className="flex items-center gap-4">
      <ResponsiveContainer width="50%" height={150}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={40}
            outerRadius={60}
            paddingAngle={5}
            dataKey="value"
          >
            {chartData.map((_, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[index % COLORS.length]}
              />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              backgroundColor: "#1A1F2C",
              border: "1px solid #9B87F5",
              borderRadius: "8px",
              fontFamily: "monospace",
            }}
          />
        </PieChart>
      </ResponsiveContainer>
      <div className="flex-1 space-y-2">
        {chartData.map((item, index) => (
          <div
            key={item.name}
            className="flex items-center gap-2 text-sm font-mono"
          >
            <div
              className="w-3 h-3 rounded"
              style={{ backgroundColor: COLORS[index % COLORS.length] }}
            />
            <span className="text-[#8E9196]">{item.name}</span>
            <span className="text-white ml-auto">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

interface BrowserChartProps {
  data: Record<string, number>;
}

export function BrowserChart({ data }: BrowserChartProps) {
  const chartData = Object.entries(data)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 5);

  if (chartData.length === 0) {
    return (
      <div className="h-[150px] flex items-center justify-center text-[#8E9196] font-mono text-sm">
        NO_DATA_AVAILABLE
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={150}>
      <BarChart data={chartData} layout="vertical">
        <XAxis
          type="number"
          stroke="#8E9196"
          fontSize={10}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          type="category"
          dataKey="name"
          stroke="#8E9196"
          fontSize={10}
          tickLine={false}
          axisLine={false}
          width={60}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: "#1A1F2C",
            border: "1px solid #9B87F5",
            borderRadius: "8px",
            fontFamily: "monospace",
          }}
        />
        <Bar dataKey="value" fill="#9B87F5" radius={[0, 4, 4, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

interface RefererListProps {
  data: Record<string, number>;
}

export function RefererList({ data }: RefererListProps) {
  const sortedData = Object.entries(data)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  if (sortedData.length === 0) {
    return (
      <div className="text-[#8E9196] font-mono text-sm">
        NO_REFERRERS_TRACKED
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {sortedData.map(([referer, count], index) => (
        <motion.div
          key={referer}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.1 }}
          className="flex items-center justify-between p-2 bg-[#0a0a0a] rounded border border-[#9B87F5]/10"
        >
          <span className="text-[#8E9196] font-mono text-xs truncate max-w-[200px]">
            {referer}
          </span>
          <span className="text-[#9B87F5] font-mono text-sm font-bold">
            {count}
          </span>
        </motion.div>
      ))}
    </div>
  );
}

interface StatsCardProps {
  title: string;
  value: string | number;
  icon?: React.ReactNode;
  ascii?: string;
}

export function StatsCard({ title, value, icon, ascii }: StatsCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 bg-[#1A1F2C]/50 rounded-lg border border-[#9B87F5]/10"
    >
      {ascii && (
        <pre className="font-mono text-[#9B87F5]/30 text-[8px] mb-2">
          {ascii}
        </pre>
      )}
      <div className="flex items-center gap-3">
        {icon && (
          <div className="w-10 h-10 rounded-lg bg-[#9B87F5]/10 flex items-center justify-center">
            {icon}
          </div>
        )}
        <div>
          <p className="text-[#8E9196] font-mono text-xs">{title}</p>
          <p className="text-2xl font-bold text-white font-mono">{value}</p>
        </div>
      </div>
    </motion.div>
  );
}
